/**
 * useGroupParticipantAvatars — resuelve `participantKey → avatarUrl` para
 * los mensajes de grupo del thread actual.
 *
 * La key es la misma que el backend persiste en wa_contact_avatars.phone:
 *   - phone real → solo dígitos (sin '+')
 *   - JID `@lid` (privacy mode) → "lid:XXX"
 *
 * Estrategia:
 *   1. Recopila keys únicas de los mensajes visibles.
 *   2. SELECT batch a wa_contact_avatars filtrando por esas keys.
 *   3. Suscribe realtime a INSERT/UPDATE → cuando el background job del
 *      webhook termina de subir la foto, el thread la refleja sin reload.
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

interface MsgLike {
  group_participant_phone?: string | null;
  group_participant_jid?: string | null;
}

/**
 * Deriva la cache key estable. Mismo algoritmo que
 * supabase/functions/_shared/waContactAvatar.ts:deriveCacheKey.
 */
export function deriveParticipantAvatarKey(
  phone: string | null | undefined,
  jid: string | null | undefined,
): string | null {
  if (phone) {
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 7 && digits.length <= 15) return digits;
  }
  if (jid) {
    if (jid.endsWith('@lid')) {
      const lid = jid.replace('@lid', '').replace(/\D/g, '');
      if (lid.length > 0) return `lid:${lid}`;
    }
    if (jid.includes('@s.whatsapp.net') || jid.includes('@c.us')) {
      const digits = jid.replace(/@.*/, '').replace(/:.+$/, '').replace(/\D/g, '');
      if (digits.length >= 7 && digits.length <= 15) return digits;
    }
  }
  return null;
}

export function useGroupParticipantAvatars(
  tenantId: string | null,
  messages: MsgLike[],
): Record<string, string | null> {
  const [avatars, setAvatars] = useState<Record<string, string | null>>({});

  // Keys únicas del thread (estabiliza dependencia para no re-fetch al
  // recibir un mensaje sin participants nuevos).
  const keysCsv = useMemo(() => {
    const set = new Set<string>();
    for (const m of messages) {
      const k = deriveParticipantAvatarKey(m.group_participant_phone, m.group_participant_jid);
      if (k) set.add(k);
    }
    return [...set].sort().join(',');
  }, [messages]);

  useEffect(() => {
    // Stale-while-revalidate: NO reseteamos a {} cuando no hay keys
    // (transición al abrir el grupo: messages=[] momentáneo, o chat 1:1).
    // Resetear causaba un flash visible iniciales→fotos cada vez que se
    // entraba a un grupo. Los avatares viejos son inocuos (las keys son
    // por phone único; un participante nuevo no matchea una key vieja →
    // fallback a iniciales hasta que el fetch nuevo complete).
    if (!tenantId || !keysCsv) return;
    const keys = keysCsv.split(',');

    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('wa_contact_avatars')
        .select('phone, avatar_url')
        .eq('tenant_id', tenantId)
        .in('phone', keys);
      if (cancelled) return;
      const map: Record<string, string | null> = {};
      for (const row of (data ?? []) as Array<{ phone: string; avatar_url: string | null }>) {
        map[row.phone] = row.avatar_url;
      }
      setAvatars(map);
    })();

    // Realtime: el background fetch del webhook hace UPSERT cuando termina
    // de subir la foto. Ese INSERT/UPDATE llega aquí y actualiza la UI.
    const channel = supabase
      .channel(`wa_avatars_${tenantId}_${keysCsv}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'wa_contact_avatars', filter: `tenant_id=eq.${tenantId}` },
        (payload) => {
          const row = payload.new as { phone?: string; avatar_url?: string | null } | null;
          if (!row?.phone) return;
          if (!keys.includes(row.phone)) return;
          setAvatars((prev) => ({ ...prev, [row.phone!]: row.avatar_url ?? null }));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [tenantId, keysCsv]);

  return avatars;
}
